import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

// Public pages render their own unauthenticated UI; bouncing them
// to /login on a routine 401 just reloads the page they're already on
// and AuthContext's mount-time session probe loops forever. The marketing
// root ('/') is public too — it must be an exact match, not a prefix, or
// every path would qualify.
const PUBLIC_AUTH_PATH_PREFIXES = [
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/account-setup',
  '/auth/',
  '/invite/',
  '/verify-email',
  '/privacy',
  '/terms',
  '/blog',
];

export function isOnPublicAuthPage(pathname: string): boolean {
  if (pathname === '/') return true;
  return PUBLIC_AUTH_PATH_PREFIXES.some((p) => pathname.startsWith(p));
}

// Endpoints where a 401 means "bad credentials" rather than "expired session."
// Running the refresh dance against these would 401 again on /auth/refresh
// and surface "Invalid refresh token" to the user, masking the real reason
// their login attempt failed.
const CREDENTIAL_ENDPOINT_PATTERNS = [
  /\/auth\/login$/,
  /\/auth\/refresh$/,
  /\/auth\/oauth\/[^/]+\/callback$/,
  /\/auth\/magic-link\/consume$/,
];

function isCredentialEndpoint(url: string | undefined): boolean {
  if (!url) return false;
  return CREDENTIAL_ENDPOINT_PATTERNS.some((re) => re.test(url));
}

// `withCredentials: true` is required for the browser to send/receive the
// httpOnly auth cookies set by the API. Pair with the API's CORS allow-list
// + `credentials: true` so the cross-origin request is permitted.
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Refresh coordination — when one 401 fires the refresh flow, queue other
// in-flight requests until the new access cookie lands, then retry them all.
let isRefreshing = false;
let failedQueue: Array<{
  resolve: () => void;
  reject: (error: Error) => void;
}> = [];

const processQueue = (error: Error | null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve();
  });
  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isCredentialEndpoint(originalRequest.url)
    ) {
      if (isRefreshing) {
        return new Promise<void>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => apiClient(originalRequest))
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // The refresh cookie is sent automatically with `withCredentials: true`.
        // No need to pull a token out of localStorage — there isn't one anymore.
        await axios.post(
          `${API_BASE_URL}/auth/refresh`,
          {},
          { withCredentials: true },
        );
        processQueue(null);
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as Error);
        // Cookies the API already invalidated on its side; the client just
        // needs to bounce the user to /login — but only if they were on a
        // protected page. Bouncing a user who's already on /login (or any
        // public auth page) reloads the page they're already viewing, and
        // AuthContext's mount-time session probe would loop forever.
        if (!isOnPublicAuthPage(window.location.pathname)) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // A lapsed Gooey+ user reaching a non-primary account gets 403
    // SUBSCRIPTION_REQUIRED from the server. Bounce them to the billing page to
    // resubscribe — the global safety net behind the per-component handling.
    // Guard against bouncing while already on /billing (which itself issues
    // account-scoped calls) so we don't loop.
    const code = (error.response?.data as { code?: string } | undefined)?.code;
    if (
      error.response?.status === 403 &&
      code === 'SUBSCRIPTION_REQUIRED' &&
      window.location.pathname !== '/billing'
    ) {
      window.location.href = '/billing';
    }

    return Promise.reject(error);
  },
);
